import { Fragment } from "react";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  XMarkIcon,
  EllipsisVerticalIcon,
  SunIcon,
  MoonIcon,
} from "@heroicons/react/24/outline";
import { Menu, Transition } from "@headlessui/react";
import { copyImageToClipboard, copyTextToClipboard } from "./utils";

const ControlBar = ({
  instance,
  images,
  navigationState,
  loading,
  onNavigate,
  onClose,
  darkMode,
  onToggleDarkMode,
  currentImageIndex,
}) => {
  const artwork = instance?.edition?.artwork;

  return (
    <div className="flex shrink-0 justify-end landscape:absolute landscape:right-0 landscape:top-0 landscape:z-10">
      <div
        className={`flex items-center gap-1 rounded-bl-lg p-1.5 backdrop-blur-md ${darkMode ? "bg-neutral-900/90" : "bg-white/90"}`}
      >
        <Menu as="div" className="relative">
          <Menu.Button
            className={`flex min-h-[44px] min-w-[44px] items-center justify-center rounded border transition-colors ${darkMode ? "border-gray-600 text-gray-300 hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
          >
            <EllipsisVerticalIcon className="h-5 w-5" />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items
              className={`absolute right-0 z-20 mt-1 w-44 origin-top-right rounded border py-1 shadow-lg ${darkMode ? "border-gray-700 bg-neutral-900" : "border-gray-200 bg-white"}`}
            >
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => {
                      const current = images[currentImageIndex] ?? images[0];
                      const src = current?.imgX || current?.imgT;
                      copyImageToClipboard(src);
                    }}
                    className={`${active ? (darkMode ? "bg-gray-800" : "bg-gray-50") : ""} block w-full px-4 py-2 text-left text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                  >
                    Copy Image
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() =>
                      copyTextToClipboard(
                        artwork?.description || artwork?.precis || "",
                      )
                    }
                    className={`${active ? (darkMode ? "bg-gray-800" : "bg-gray-50") : ""} block w-full px-4 py-2 text-left text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                  >
                    Copy Description
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <a
                    href={`/artworks/${artwork?.id}/offers`}
                    className={`${active ? (darkMode ? "bg-gray-800" : "bg-gray-50") : ""} block w-full px-4 py-2 text-left text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                  >
                    Create Offer
                  </a>
                )}
              </Menu.Item>
            </Menu.Items>
          </Transition>
        </Menu>
        <button
          onClick={onToggleDarkMode}
          className={`flex min-h-[44px] min-w-[44px] items-center justify-center rounded border transition-colors ${darkMode ? "border-gray-600 text-gray-300 hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
          title={darkMode ? "Light mode" : "Dark mode"}
        >
          {darkMode ? (
            <SunIcon className="h-5 w-5" />
          ) : (
            <MoonIcon className="h-5 w-5" />
          )}
        </button>
        <button
          onClick={() => onNavigate("previous")}
          disabled={!navigationState?.hasPrevious || loading}
          className={`flex min-h-[44px] min-w-[44px] items-center justify-center rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${darkMode ? "border-gray-600 text-gray-300 hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
          title="Previous instance (Arrow Up)"
        >
          <ChevronUpIcon className="h-5 w-5" />
        </button>
        <button
          onClick={() => onNavigate("next")}
          disabled={!navigationState?.hasNext || loading}
          className={`flex min-h-[44px] min-w-[44px] items-center justify-center rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${darkMode ? "border-gray-600 text-gray-300 hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
          title="Next instance (Arrow Down)"
        >
          <ChevronDownIcon className="h-5 w-5" />
        </button>
        <button
          onClick={onClose}
          className={`ml-1 flex min-h-[44px] min-w-[44px] items-center justify-center rounded transition-colors ${darkMode ? "text-gray-400 hover:bg-gray-800 hover:text-gray-200" : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"}`}
          title="Close (Escape)"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default ControlBar;
